// frontend/src/layouts/TechnicianLayout.js

import React, { useState, useEffect } from 'react';
import {
    Box,
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Avatar,
    Menu,
    MenuItem,
    Badge,
    Divider,
    useTheme,
    useMediaQuery,
    Chip,
    Alert,
    Snackbar
} from '@mui/material';
import {
    Menu as MenuIcon,
    Dashboard,
    Assignment,
    Build,
    Inventory,
    Assessment,
    Person,
    ExitToApp,
    Notifications,
    LocationOn,
    Phone,
    Schedule,
    ChevronLeft
} from '@mui/icons-material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useTechnicianAuth } from '../contexts/TechnicianAuthContext';

const DRAWER_WIDTH = 280;

const TechnicianLayout = () => {
    const [drawerOpen, setDrawerOpen] = useState(false); // CAMBIADO: Inicia cerrado
    const [anchorEl, setAnchorEl] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [workSessionActive, setWorkSessionActive] = useState(false);
    const [locationStatus, setLocationStatus] = useState('unknown');
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [companySettings, setCompanySettings] = useState({ company_name: 'YinoWISP', company_logo: '' });

    useEffect(() => {
        const fetchCompanySettings = async () => {
            try {
                const response = await fetch('/api/public/company-settings');
                if (response.ok) {
                    const data = await response.json();
                    setCompanySettings({
                        company_name: data.company_name || 'YinoWISP',
                        company_logo: data.company_logo || ''
                    });
                }
            } catch (error) {
                console.error('Error fetching company settings for technician layout:', error);
            }
        };
        fetchCompanySettings();
    }, []);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const location = useLocation();
    const { technician, logout } = useTechnicianAuth();

    useEffect(() => {
        // En mobile, el drawer debe estar cerrado por defecto
        if (isMobile) {
            setDrawerOpen(false);
        }

        // Verificar sesión de trabajo activa
        const activeSession = localStorage.getItem('activeWorkSession');
        setWorkSessionActive(!!activeSession);

        // Verificar estado de GPS
        checkLocationStatus();

        // Configurar actualización periódica del GPS
        const locationInterval = setInterval(checkLocationStatus, 30000);

        return () => clearInterval(locationInterval);
    }, [isMobile]);

    const checkLocationStatus = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                () => setLocationStatus('active'),
                () => setLocationStatus('inactive'),
                { timeout: 5000 }
            );
        } else {
            setLocationStatus('unavailable');
        }
    };

    const menuItems = [
        {
            text: 'Dashboard',
            icon: <Dashboard />,
            path: '/technician/dashboard',
            badge: null
        },
        {
            text: 'Instalaciones',
            icon: <Assignment />,
            path: '/technician/installations',
            badge: null
        },
        {
            text: 'Soporte',
            icon: <Build />,
            path: '/technician/tickets',
            badge: null
        },
        {
            text: 'Mi Inventario',
            icon: <Inventory />,
            path: '/technician/inventory',
            badge: null
        },
        {
            text: 'Reportes',
            icon: <Assessment />,
            path: '/technician/reports',
            badge: null
        },
        {
            text: 'Horario',
            icon: <Schedule />,
            path: '/technician/schedule',
            badge: null
        }
    ];

    const handleDrawerToggle = () => {
        setDrawerOpen(!drawerOpen);
    };

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/technician/login');
        } catch (error) {
            console.error('Error en logout:', error);
        }
        handleMenuClose();
    };

    const handleNavigate = (path) => {
        navigate(path);
        // CORREGIDO: En mobile, cerrar drawer después de navegar
        if (isMobile) {
            setDrawerOpen(false);
        }
    };

    const isActiveRoute = (path) => {
        return location.pathname.startsWith(path);
    };

    const showAlert = (message) => {
        setAlertMessage(message);
        setAlertOpen(true);
    };

    const drawer = (
        <Box sx={{ width: DRAWER_WIDTH, height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header del drawer */}
            <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" gap={2}>
                        <Avatar sx={{ bgcolor: 'white', color: 'primary.main', width: 40, height: 40 }}>
                            {technician?.name?.charAt(0) || 'T'}
                        </Avatar>
                        <Box>
                            <Typography variant="subtitle1" noWrap>
                                {technician?.name || 'Técnico'}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                Técnico de Campo
                            </Typography>
                        </Box>
                    </Box>

                    {/* Botón para cerrar drawer en desktop */}
                    {!isMobile && (
                        <IconButton
                            onClick={handleDrawerToggle}
                            sx={{ color: 'white' }}
                            size="small"
                        >
                            <ChevronLeft />
                        </IconButton>
                    )}
                </Box>

                {/* Estado de trabajo y GPS */}
                <Box display="flex" gap={1} mt={2} flexWrap="wrap">
                    <Chip
                        size="small"
                        label={workSessionActive ? 'En Servicio' : 'Libre'}
                        color={workSessionActive ? 'success' : 'default'}
                        variant="outlined"
                        sx={{
                            color: 'white',
                            borderColor: 'rgba(255,255,255,0.5)',
                            fontSize: '0.75rem'
                        }}
                    />
                    <Chip
                        size="small"
                        icon={<LocationOn sx={{ fontSize: '0.875rem' }} />}
                        label={locationStatus === 'active' ? 'GPS' : 'Sin GPS'}
                        color={locationStatus === 'active' ? 'success' : 'warning'}
                        variant="outlined"
                        sx={{
                            color: 'white',
                            borderColor: 'rgba(255,255,255,0.5)',
                            fontSize: '0.75rem'
                        }}
                    />
                </Box>
            </Box>

            <Divider />

            {/* Lista de navegación */}
            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                <List>
                    {menuItems.map((item) => (
                        <ListItem
                            button
                            key={item.text}
                            onClick={() => handleNavigate(item.path)}
                            selected={isActiveRoute(item.path)}
                            sx={{
                                '&.Mui-selected': {
                                    bgcolor: 'primary.light',
                                    color: 'primary.contrastText',
                                    '& .MuiListItemIcon-root': {
                                        color: 'primary.contrastText',
                                    },
                                },
                                '&:hover': {
                                    bgcolor: 'action.hover',
                                },
                            }}
                        >
                            <ListItemIcon>
                                {item.badge ? (
                                    <Badge badgeContent={item.badge} color="error">
                                        {item.icon}
                                    </Badge>
                                ) : (
                                    item.icon
                                )}
                            </ListItemIcon>
                            <ListItemText primary={item.text} />
                        </ListItem>
                    ))}
                </List>
            </Box>

            <Divider />

            {/* Información adicional */}
            <Box sx={{ p: 2 }}>
                <Typography variant="caption" color="textSecondary">
                    {companySettings.company_name} Portal Técnico v1.0
                </Typography>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            {/* App Bar */}
            <AppBar
                position="fixed"
                sx={{
                    zIndex: theme.zIndex.drawer + 1,
                    bgcolor: 'primary.main'
                }}
            >
                <Toolbar>
                    <IconButton
                        color="inherit"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2 }}
                    >
                        <MenuIcon />
                    </IconButton>

                    {companySettings.company_logo && (
                        <img 
                            src={companySettings.company_logo} 
                            alt="Logo" 
                            style={{ height: 28, marginRight: 10, objectFit: 'contain' }} 
                        />
                    )}
                    <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
                        {companySettings.company_name} - Portal Técnico
                    </Typography>

                    {/* Indicadores de estado en el header (solo en mobile) */}
                    {isMobile && (
                        <Box display="flex" alignItems="center" gap={1} mr={2}>
                            {workSessionActive && (
                                <Chip
                                    size="small"
                                    label="En Servicio"
                                    color="success"
                                    variant="outlined"
                                    sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}
                                />
                            )}

                            {locationStatus === 'active' && (
                                <IconButton color="inherit" size="small">
                                    <LocationOn />
                                </IconButton>
                            )}
                        </Box>
                    )}

                    {/* Notificaciones y perfil */}
                    <IconButton color="inherit" sx={{ mr: 1 }}>
                        <Badge badgeContent={notifications.length} color="error">
                            <Notifications />
                        </Badge>
                    </IconButton>

                    <IconButton color="inherit" onClick={handleMenuClick}>
                        <Avatar
                            sx={{ width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.2)' }}
                        >
                            {technician?.name?.charAt(0) || 'T'}
                        </Avatar>
                    </IconButton>
                </Toolbar>
            </AppBar>

            {/* Menu del perfil */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <MenuItem onClick={() => { handleNavigate('/technician/profile'); handleMenuClose(); }}>
                    <ListItemIcon>
                        <Person fontSize="small" />
                    </ListItemIcon>
                    Mi Perfil
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                    <ListItemIcon>
                        <ExitToApp fontSize="small" sx={{ color: 'error.main' }} />
                    </ListItemIcon>
                    Cerrar Sesión
                </MenuItem>
            </Menu>

            {/* Drawer de navegación */}
            <Drawer
                variant={isMobile ? 'temporary' : 'persistent'}
                open={drawerOpen}
                onClose={handleDrawerToggle}
                ModalProps={{
                    keepMounted: true, // Mejor rendimiento en móviles
                }}
                sx={{
                    width: drawerOpen ? DRAWER_WIDTH : 0,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: DRAWER_WIDTH,
                        boxSizing: 'border-box',
                        transition: theme.transitions.create('width', {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.enteringScreen,
                        }),
                    },
                }}
            >
                {drawer}
            </Drawer>

            {/* Contenido principal - CORREGIDO */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    width: {
                        xs: '100%',
                        md: drawerOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : '100%'
                    },
                    ml: {
                        xs: 0,
                        md: drawerOpen ? 0 : 0
                    },
                    mt: '64px', // Altura del AppBar
                    minHeight: 'calc(100vh - 64px)',
                    bgcolor: 'background.default',
                    transition: theme.transitions.create(['margin', 'width'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                }}
            >
                <Outlet />
            </Box>

            {/* Snackbar para alertas */}
            <Snackbar
                open={alertOpen}
                autoHideDuration={6000}
                onClose={() => setAlertOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setAlertOpen(false)}
                    severity="info"
                    sx={{ width: '100%' }}
                >
                    {alertMessage}
                </Alert>
            </Snackbar>

            {/* Botón de emergencia (solo móvil) */}
            {isMobile && (
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: 16,
                        right: 16,
                        zIndex: theme.zIndex.fab
                    }}
                >
                    <IconButton
                        color="error"
                        sx={{
                            bgcolor: 'error.main',
                            color: 'white',
                            width: 56,
                            height: 56,
                            '&:hover': {
                                bgcolor: 'error.dark',
                            },
                        }}
                        onClick={() => window.open('tel:911')}
                    >
                        <Phone />
                    </IconButton>
                </Box>
            )}
        </Box>
    );
};

export default TechnicianLayout;